/**
 * Home Component
 * Dashboard for authenticated users
 */

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { FiTrendingUp, FiUser, FiFileText, FiClock, FiShoppingCart } from "react-icons/fi";
import Profile from '../../assets/profile.jpg';
import avatar from '../../assets/Profile_avatar_placeholder.png';
import EditIcon from '../../assets/mynaui_edit.png';
import CurrentPlansTab from '../CurrentPlansTab/CurrentPlansTab';
import HistoryTab from '../HistoryTab/HistoryTab';
import CartTab from '../CartTab/CartTab';
import ProfileTab from '../Profile/profile';
import ContactUs from '../ContactUs/ContactUs';
import { rules } from '../../utils/validation';
import './home.css';

const Home = () => {
  const { user, logout, updateProfile } = useAuth();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('profile');
  const [mainTab, setMainTab] = useState('profile');
  const [loggingOut, setLoggingOut] = useState(false);
  const [isEditingPersonalInfo, setIsEditingPersonalInfo] = useState(false);
  const [personalInfoForm, setPersonalInfoForm] = useState({
    fullName: '',
    email: '',
    mobile: ''
  });
  const [personalInfoErrors, setPersonalInfoErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Handle URL tab parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam && ['profile', 'plans', 'history', 'cart'].includes(tabParam)) {
      setActiveTab(tabParam);
      setMainTab('profile'); // Ensure we're in dashboard view

      // Auto-scroll to top when cart tab is activated via URL parameter
      if (tabParam === 'cart') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, [location.search]);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setLoggingOut(false);
    }
  };

  // Get display values
  const displayName = user?.fullName || 'User';
  const displayEmail = user?.email || '';
  const displayMobile = user?.mobile ? `+91 ${user.mobile}` : '';
  const displayRole = user?.roleName || 'User';
  const displayCompany = user?.companyName || '';
  const displayDesignation = user?.designation || displayRole;

  // Initialize form when user data is available
  useEffect(() => {
    if (user && !isEditingPersonalInfo) {
      setPersonalInfoForm({
        fullName: user?.fullName || '',
        email: user?.email || '',
        mobile: user?.mobile || ''
      });
      setPersonalInfoErrors({});
    }
  }, [user, isEditingPersonalInfo]);

  // Handle edit mode toggle
  const handleEditPersonalInfo = () => {
    setIsEditingPersonalInfo(true);
    setPersonalInfoForm({
      fullName: user?.fullName || '',
      email: user?.email || '',
      mobile: user?.mobile || ''
    });
    setPersonalInfoErrors({});
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditingPersonalInfo(false);
    setPersonalInfoForm({
      fullName: user?.fullName || '',
      email: user?.email || '',
      mobile: user?.mobile || ''
    });
    setPersonalInfoErrors({});
  };

  // Handle form input change
  const handlePersonalInfoChange = (field, value) => {
    setPersonalInfoForm(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field
    if (personalInfoErrors[field]) {
      setPersonalInfoErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Validate form
  const validatePersonalInfo = () => {
    const errors = {};

    // Validate full name
    if (!personalInfoForm.fullName || personalInfoForm.fullName.trim().length < rules.fullName.minLength) {
      errors.fullName = rules.fullName.message;
    } else if (personalInfoForm.fullName.length > rules.fullName.maxLength) {
      errors.fullName = rules.fullName.message;
    }

    // Validate email
    if (!personalInfoForm.email) {
      errors.email = 'Email is required';
    } else if (!rules.email.pattern.test(personalInfoForm.email)) {
      errors.email = rules.email.message;
    }

    // Validate mobile (optional, but if provided, must be valid)
    if (personalInfoForm.mobile) {
      const mobileDigits = personalInfoForm.mobile.replace(/\D/g, '');
      if (mobileDigits.length !== 10 || !rules.mobile.pattern.test(mobileDigits)) {
        errors.mobile = rules.mobile.message;
      }
    }

    setPersonalInfoErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle save
  const handleSavePersonalInfo = async () => {
    if (!validatePersonalInfo()) {
      return;
    }

    setIsSaving(true);
    try {
      const updateData = {
        fullName: personalInfoForm.fullName.trim()
      };

      // Only include email if it changed
      if (personalInfoForm.email !== user?.email) {
        updateData.email = personalInfoForm.email.trim();
      }

      const response = await updateProfile(updateData);

      if (response.success) {
        setIsEditingPersonalInfo(false);
        setPersonalInfoErrors({});
      } else {
        setPersonalInfoErrors({
          general: response.message || 'Failed to update profile'
        });
      }
    } catch (error) {
      setPersonalInfoErrors({
        general: error.message || 'An error occurred while updating profile'
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <main className="home">
      <section className="hero">
        <div className="container">
          <div className="hero-container">
            <div className="hero-text">
              <h1>Welcome, {displayName.split(' ')[0]}!</h1>
              <p>Track your plans, manage documents, and stay on top of your compliance journey</p>
            </div>
          </div>
        </div>
      </section>

      {/* Profile summary bar */}
      <section className="profile-summary">
        <div className="container">
          <div className="row profile-main">
            <div className="col-lg-8 col-md-8 col-sm-8 col-xs-12 profile-inner">
              <div className="profile-img">
                <img className="avatar" src={avatar} alt={displayName} />
              </div>
              <div className="user-profile">
                <div className="profile-name">{displayName}</div>
                <div className="profile-sub">{displayDesignation}</div>
                {displayCompany && <div className="profile-company">{displayCompany}</div>}
              </div>
              <div className="metric-main">
                <div className="metric">
                  <strong>0</strong>
                  <FiTrendingUp style={{ color: "#03C0A7", fontWeight: "600" }} />
                </div>
                <div className="metric-inner">Orders</div>
              </div>
            </div>
            <div className="col-lg-4 col-md-4 col-sm-4 col-xs-12 text-md-end">
              <button
                className="btn btn-teal logout-btn"
                onClick={handleLogout}
                disabled={loggingOut}
              >
                {loggingOut ? (
                  <>Logging out...</>
                ) : (
                  <>Logout</>
                )}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Personal info card */}
      <section className="personal-info">
        <div className="container">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <div className="card-text">
                <span>Personal Info</span>
              </div>
              <div className="card-button">
                {!isEditingPersonalInfo ? (
                  <button className="btn btn-light btn-sm" onClick={handleEditPersonalInfo}>
                    <img src={EditIcon} alt="Edit" className="edit-icon" />
                    <span className="me-1">Edit</span>
                  </button>
                ) : (
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-light btn-sm"
                      onClick={handleCancelEdit}
                      disabled={isSaving}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-teal btn-sm"
                      onClick={handleSavePersonalInfo}
                      disabled={isSaving}
                    >
                      {isSaving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="card-body">
              {personalInfoErrors.general && (
                <div className="alert alert-danger mb-3" role="alert">
                  {personalInfoErrors.general}
                </div>
              )}
              <div className="row gy-3">
                <div className="col-md-4">
                  <div className="label">Full Name</div>
                  {isEditingPersonalInfo ? (
                    <div>
                      <input
                        type="text"
                        className={`form-control ${personalInfoErrors.fullName ? 'is-invalid' : ''}`}
                        value={personalInfoForm.fullName}
                        onChange={(e) => handlePersonalInfoChange('fullName', e.target.value)}
                        placeholder="Enter full name"
                      />
                      {personalInfoErrors.fullName && (
                        <div className="invalid-feedback d-block">
                          {personalInfoErrors.fullName}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="value">{displayName}</div>
                  )}
                </div>
                <div className="col-md-4">
                  <div className="label">Email</div>
                  {isEditingPersonalInfo ? (
                    <div>
                      <input
                        type="email"
                        className={`form-control ${personalInfoErrors.email ? 'is-invalid' : ''}`}
                        value={personalInfoForm.email}
                        onChange={(e) => handlePersonalInfoChange('email', e.target.value)}
                        placeholder="Enter email"
                      />
                      {personalInfoErrors.email && (
                        <div className="invalid-feedback d-block">
                          {personalInfoErrors.email}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="value">{displayEmail}</div>
                  )}
                </div>
                <div className="col-md-4">
                  <div className="label">Phone</div>
                  {isEditingPersonalInfo ? (
                    <div>
                      <input
                        type="text"
                        className={`form-control ${personalInfoErrors.mobile ? 'is-invalid' : ''}`}
                        value={personalInfoForm.mobile}
                        onChange={(e) => {
                          // Only allow digits
                          const value = e.target.value.replace(/\D/g, '');
                          if (value.length <= 10) {
                            handlePersonalInfoChange('mobile', value);
                          }
                        }}
                        placeholder="Enter 10-digit mobile number"
                        maxLength="10"
                      />
                      {personalInfoErrors.mobile && (
                        <div className="invalid-feedback d-block">
                          {personalInfoErrors.mobile}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="value">{displayMobile}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Tab Bar */}
      <section className="main-tabs-section">
        <div className="container">
          <div className="main-tabs">
            <button
              className={`main-tab-btn ${mainTab === 'profile' ? 'active' : ''}`}
              onClick={() => setMainTab('profile')}
            >
              Dashboard
            </button>
            <button
              className={`main-tab-btn ${mainTab === 'article' ? 'active' : ''}`}
              onClick={() => setMainTab('article')}
            >
              Services
            </button>
          </div>
        </div>
      </section>

      {/* Main Content based on Main Tab */}
      {mainTab === 'profile' && (
        <section className="dashboard-layout">
          <div className="container">
            <div className="row">
              {/* Left Sidebar */}
              <div className="col-lg-3 col-md-3 col-sm-12 sidebar">
                <div className="sidebar-nav">
                  <button
                    className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`}
                    onClick={() => setActiveTab('profile')}
                  >
                    <FiUser className="nav-icon" />
                    <span>Profile</span>
                  </button>
                  <button
                    className={`nav-item ${activeTab === 'plans' ? 'active' : ''}`}
                    onClick={() => setActiveTab('plans')}
                  >
                    <FiFileText className="nav-icon" />
                    <span>Current Plans</span>
                  </button>
                  <button
                    className={`nav-item ${activeTab === 'history' ? 'active' : ''}`}
                    onClick={() => setActiveTab('history')}
                  >
                    <FiClock className="nav-icon" />
                    <span>History</span>
                  </button>
                  <button
                    className={`nav-item ${activeTab === 'cart' ? 'active' : ''}`}
                    onClick={() => setActiveTab('cart')}
                  >
                    <FiShoppingCart className="nav-icon" />
                    <span>Cart</span>
                  </button>
                </div>
              </div>

              {/* Main Content Area */}
              <div className="col-lg-9 col-md-9 col-sm-12 main-content-area">
                {activeTab === 'profile' && <ProfileTab />}

                {activeTab === 'plans' && <CurrentPlansTab />}

                {activeTab === 'history' && <HistoryTab />}

                {activeTab === 'cart' && <CartTab />}
              </div>
            </div>
          </div>
        </section>
      )}

      {mainTab === 'article' && (
        <section className="article-content">
          <div className="container">
            <div className="row">
              <div className="col-12">
                <div className="services-placeholder">
                  <h3>Available Services</h3>
                  <p>Browse our corporate governance services and manage your compliance needs.</p>
                  <a href="/services" className="btn btn-teal">
                    View Services
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Contact Us Section - Only show on Profile tab */}
      {activeTab === 'profile' && <ContactUs />}
    </main>
  );
};

export default Home;
